import { DatePicker } from "@mui/x-date-pickers";
import { format, isValid } from "date-fns";
import { ReactNode } from "react";
import { isoDateFormat } from "utils/dateUtils";
import { toDateOnly } from "./dateOnlyField";

type DateOnlyPickerProps = {
  label: string;
  /** yyyy-MM-dd, the shape every date on this page is held in. */
  value?: string;
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: ReactNode;
};

/**
 * The calendar picker KAdmin uses everywhere else, speaking the yyyy-MM-dd strings the authoring forms
 * hold. Dates are kept as local midnight inside the picker, because that is what the generated client
 * serializes a DateOnly from; the conversion at the edges is the same one the plain strings needed.
 */
const DateOnlyPicker = ({
  label,
  value = "",
  onChange,
  required,
  disabled,
  error,
  helperText,
}: DateOnlyPickerProps) => (
  <DatePicker
    label={label}
    disabled={disabled}
    format="MM/dd/yyyy"
    value={toDateOnly(value) ?? null}
    onChange={(date) => {
      // Clearing the picker hands over null, which is an empty field. A half-typed date is not a
      // value yet, so the field keeps what it had until the entry is complete.
      if (date === null) {
        onChange("");
        return;
      }

      if (isValid(date)) {
        onChange(format(date, isoDateFormat));
      }
    }}
    slotProps={{
      actionBar: { actions: ["clear", "cancel"] },
      textField: {
        variant: "standard",
        fullWidth: true,
        required,
        error,
        helperText,
      },
    }}
  />
);

export default DateOnlyPicker;
